import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { de, es, fr, it, ja, nl, pt, sv } from 'date-fns/locale';
import type { Locale } from 'date-fns/locale';

import { cn } from '../lib/utils';
import { buttonVariants } from './button';

/** Map language codes to date-fns locales. English is date-fns default (no import needed). */
const DATE_FNS_LOCALES: Record<string, Locale> = { de, es, fr, it, ja, nl, pt, sv };

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: 'outline' | 'ghost';
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  fixedWeeks = true,
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames();
  // Auto-detect locale from <html lang="..."> unless explicitly provided via props
  const autoLocale = DATE_FNS_LOCALES[document.documentElement.lang];

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      locale={autoLocale}
      className={cn('p-3', className)}
      captionLayout={captionLayout}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('relative flex flex-col gap-4 sm:flex-row', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex h-7 w-full items-center justify-center',
          defaultClassNames.month_caption,
        ),
        caption_label: cn('text-sm font-medium', defaultClassNames.caption_label),
        table: 'w-full border-collapse space-y-1',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
          defaultClassNames.weekday,
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        day: cn('relative h-9 w-9 p-0 text-center text-sm', defaultClassNames.day),
        day_button: cn(
          'inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md p-0 font-normal transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'aria-selected:opacity-100',
        ),
        range_start: cn('rounded-l-md', defaultClassNames.range_start),
        range_end: cn('rounded-r-md', defaultClassNames.range_end),
        selected: cn(
          'rounded-md bg-primary/15 text-primary [&>button]:hover:bg-primary/25 [&>button]:hover:text-primary',
        ),
        today: cn('rounded-md bg-accent/60 text-accent-foreground [&>button]:hover:bg-accent'),
        outside: cn(
          'text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        ),
        disabled: cn('text-muted-foreground opacity-50'),
        range_middle: cn('aria-selected:bg-accent aria-selected:text-accent-foreground'),
        hidden: cn('invisible'),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...chevronProps }) =>
          orientation === 'left' ? (
            <ChevronLeftIcon className="h-4 w-4" {...chevronProps} />
          ) : (
            <ChevronRightIcon className="h-4 w-4" {...chevronProps} />
          ),
        PreviousMonthButton: (btnProps) => (
          <button {...btnProps} data-testid="calendar-prev-month" />
        ),
        NextMonthButton: (btnProps) => <button {...btnProps} data-testid="calendar-next-month" />,
        DayButton: ({ day, modifiers: _modifiers, ...btnProps }) => (
          <button
            {...btnProps}
            data-testid={`calendar-day-${day.date.toISOString().slice(0, 10)}`}
          />
        ),
        ...props.components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
